import { Injectable, inject } from '@angular/core';
import { Observable, of, from, throwError } from 'rxjs';
import { delay, switchMap, map, catchError } from 'rxjs/operators';

import {
  INBOUND_NOTICE_STATUS_OPTIONS,
  INBOUND_WARRANT_STATUS_OPTIONS,
  INBOUND_TYPE_OPTIONS,
} from './inbound.config';
import { WarehouseWarrantControllerService } from '@app/api/stock-service/api/warehouseWarrantController.service';
import { WarehouseResultControllerService } from '@app/api/stock-service/api/warehouseResultController.service';
import { PageInfoWarehouseWarrantVO } from '@app/api/stock-service/model/pageInfoWarehouseWarrantVO';
import { PageInfoWarehouseResultVO } from '@app/api/stock-service/model/pageInfoWarehouseResultVO';
import { WarehouseWarrantSubmitRequestDTO } from '@app/api/stock-service/model/warehouseWarrantSubmitRequestDTO';
import { WarehouseWarrantInfoVO } from '@app/api/stock-service/model/warehouseWarrantInfoVO';
import { WarehouseResultPageQueryDTO } from '@app/api/stock-service/model/warehouseResultPageQueryDTO';

export interface InboundSearchParams {
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
  queryCode?: string;
  channelTypeCode?: string;
  storeIdList?: string; // From form input (text)
  status?: string;
  receivingLogicWarehouseCode?: string;
  beginCreationDate?: string;
  endCreationDate?: string;
}

export interface WarehouseResultSearchParams {
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
  queryCode?: string;
  channelTypeCode?: string;
  storeIdList?: string; // From form input (text)
  statuses?: string[]; // Changed from status string to statuses array
  receivingLogicWarehouseCode?: string;
  beginCreationDate?: string;
  endCreationDate?: string;
}

export interface WarehouseOption {
  code: string;
  name: string;
  id?: number;
}

@Injectable({
  providedIn: 'root',
})
export class InboundService {
  private readonly warehouseWarrantService = inject(
    WarehouseWarrantControllerService,
  );
  private readonly warehouseResultService = inject(
    WarehouseResultControllerService,
  );

  getInboundRecords(
    params: InboundSearchParams,
  ): Observable<PageInfoWarehouseWarrantVO> {
    // Convert search params to API format
    const apiParams = {
      page: params.page || 0,
      size: params.size || 20,
      sortBy: params.sortBy,
      ascending: params.ascending,
      queryCode: params.queryCode,
      channelTypeCode: params.channelTypeCode,
      storeIdList: params.storeIdList ? [params.storeIdList] : undefined,
      status: params.status,
      receivingLogicWarehouseCode: params.receivingLogicWarehouseCode,
      beginCreationDate: params.beginCreationDate,
      endCreationDate: params.endCreationDate,
    };

    const result = this.warehouseWarrantService.page1(apiParams as any);

    // Handle Blob response by converting it to JSON
    return result.pipe(
      switchMap((response: any) => {
        if (response instanceof Blob) {
          return from(response.text()).pipe(
            map((text) => JSON.parse(text)),
            catchError((error) => {
              console.error('Error parsing JSON:', error);
              return throwError(() => new Error('Failed to parse JSON'));
            }),
          );
        }
        return of(response);
      }),
    ) as any;
  }

  getWarehouseOptions(): Observable<any[]> {
    const options = [
      { label: 'Main Warehouse', value: 'WH001' },
      { label: 'Secondary Warehouse', value: 'WH002' },
      { label: 'Regional Warehouse', value: 'WH003' },
      { label: 'Distribution Center', value: 'WH004' },
    ];
    return of(options).pipe(delay(200));
  }

  getSupplierOptions(): Observable<any[]> {
    const options = [
      { label: 'ABC Electronics Ltd', value: 'SUP001' },
      { label: 'XYZ Manufacturing', value: 'SUP002' },
      { label: 'Global Supplies Co', value: 'SUP003' },
      { label: 'Tech Parts Inc', value: 'SUP004' },
      { label: 'Quality Materials', value: 'SUP005' },
    ];
    return of(options).pipe(delay(200));
  }

  getInboundNoticeStatusOptions(): Observable<any[]> {
    return of(INBOUND_NOTICE_STATUS_OPTIONS).pipe(delay(200));
  }

  getInboundWarrantStatusOptions(): Observable<any[]> {
    return of(INBOUND_WARRANT_STATUS_OPTIONS).pipe(delay(200));
  }

  getInboundTypeOptions(): Observable<any[]> {
    return of(INBOUND_TYPE_OPTIONS).pipe(delay(200));
  }

  cancelInbound(id: string): Observable<any> {
    // Mock API call - replace with actual API call
    return of({ success: true }).pipe(delay(300));
  }

  getWarehouseResultRecords(
    params: WarehouseResultSearchParams,
  ): Observable<PageInfoWarehouseResultVO> {
    const dto: WarehouseResultPageQueryDTO = {
      page: params.page || 0,
      size: params.size || 20,
      sortBy: params.sortBy || 'creationDate',
      ascending: params.ascending !== false,
      queryCode: params.queryCode,
      channelTypeCode: params.channelTypeCode,
      storeIdList: params.storeIdList
        ? [Number.parseInt(params.storeIdList)]
        : undefined,
      statuses: params.statuses?.length ? params.statuses : undefined,
      receivingLogicWarehouseCode: params.receivingLogicWarehouseCode,
      beginCreationDate: params.beginCreationDate,
      endCreationDate: params.endCreationDate,
    };

    const result = this.warehouseResultService.page2(dto);

    // Handle Blob response by converting it to JSON
    return result.pipe(
      switchMap((response: any) => {
        if (response instanceof Blob) {
          return from(response.text()).pipe(
            map((text: string) => JSON.parse(text)),
            catchError((error: any) => {
              console.error('Error parsing JSON:', error);
              return throwError(() => new Error('Failed to parse JSON'));
            }),
          );
        }
        return of(response);
      }),
    ) as any;
  }

  getSalesChannelOptions(): Observable<any[]> {
    // Mock data - replace with actual API call
    return of([
      { label: 'Online', value: 'ONLINE' },
      { label: 'Retail', value: 'RETAIL' },
      { label: 'Wholesale', value: 'WHOLESALE' },
    ]).pipe(delay(500));
  }

  getWarehouseWarrantDetail(id: number): Observable<any> {
    const result = this.warehouseWarrantService.detail(id);

    return result.pipe(
      switchMap((response: any) => {
        if (response instanceof Blob) {
          return from(response.text()).pipe(
            map((text: string) => JSON.parse(text)),
            catchError((error: any) => {
              console.error(
                'Error parsing Warehouse Warrant Detail JSON:',
                error,
              );
              return throwError(() => new Error('Failed to parse JSON'));
            }),
          );
        }
        return of(response);
      }),
    );
  }

  getWarehouseResultDetail(id: number): Observable<any> {
    const result = this.warehouseResultService.detail2(id);

    return result.pipe(
      switchMap((response: any) => {
        if (response instanceof Blob) {
          return from(response.text()).pipe(
            map((text: string) => JSON.parse(text)),
            catchError((error: any) => {
              console.error(
                'Error parsing Warehouse Result Detail JSON:',
                error,
              );
              return throwError(() => new Error('Failed to parse JSON'));
            }),
          );
        }
        return of(response);
      }),
    );
  }

  createInboundNotice(payload: any): Observable<any> {
    const result = this.warehouseWarrantService.add(
      payload,
      'response',
      false,
      {
        httpHeaderAccept: 'application/json' as any,
      },
    ) as any;

    // Handle Blob response by converting it to JSON
    return result.pipe(
      switchMap((response: any) => {
        if (response instanceof Blob) {
          return from(response.text()).pipe(
            map((text: string) => JSON.parse(text)),
            catchError((error: any) => {
              console.error('Error parsing Create Inbound Notice JSON:', error);
              return throwError(() => new Error('Failed to parse JSON'));
            }),
          );
        }
        return of(response);
      }),
    );
  }

  updateInboundStatus(id: number, status: string): Observable<any> {
    // Use real API call for updating inbound notice status
    return this.warehouseWarrantService.updateStatus(id, status);
  }

  submitInboundWarrant(
    id: number,
    submitData: WarehouseWarrantSubmitRequestDTO,
  ): Observable<WarehouseWarrantInfoVO> {
    // Use real API call for submitting inbound warrant with received quantities
    return this.warehouseWarrantService.submit(id, submitData);
  }

  /**
   * Find warehouse by code with case-insensitive fallback
   * @param warehouseCode - The warehouse code to search for
   * @param warehouses - Array of warehouse options to search in
   * @returns The found warehouse or undefined
   */
  static findWarehouse(
    warehouseCode: string,
    warehouses: WarehouseOption[],
  ): WarehouseOption | undefined {
    if (!warehouseCode || !warehouses || warehouses.length === 0) {
      return undefined;
    }

    // Try exact match first
    let warehouse = warehouses.find((wh) => wh.code === warehouseCode);

    // If not found, try case-insensitive match
    warehouse ??= warehouses.find(
      (wh) => wh.code?.toLowerCase() === warehouseCode.toLowerCase(),
    );

    return warehouse;
  }

  /**
   * Get warehouse name from warehouse code
   * @param warehouseCode - The warehouse code
   * @param warehouses - Array of warehouse options
   * @param fallbackName - Optional fallback name if warehouse not found
   * @returns The warehouse name or fallback
   */
  static getWarehouseName(
    warehouseCode: string,
    warehouses: WarehouseOption[],
    fallbackName?: string,
  ): string {
    const warehouse = InboundService.findWarehouse(warehouseCode, warehouses);
    return warehouse?.name || fallbackName || warehouseCode || '';
  }
}
